import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useMediaQuery,
  Button,
  Grid,
  useTheme
} from '@mui/material';
import ExpandIcon from '../../../assets/images/featureGrid/ExpandIcon.png';
import Vector from '../../../assets/images/featureGrid/Vector.png';
import closeIcon from '../../../assets/images/featureGrid/CloseIcon.png';
import { useTranslation } from 'react-i18next';
import { getDrupalImageUrl } from '../../../components/AppRoadMap/roadmapFallbackData';
import './FeatureGrid.scss';
// import './FeatureDesktop.css';

interface CardItem {
  id: number;
  logo: string;
  title: string;
  description: string;
  listTitle: string;
  list: string[];
}
function decodeHtmlEntities(html?: string): string {
  if (!html) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}
function stripLeadingDash(text?: string): string {
  if (!text) return '';
  return text.replace(/^[\s-–—]+/, '').trim();
}

function stripFeatureGridWrapperTags(html?: string): string {
  if (!html) return '';
  const decoded = decodeHtmlEntities(html);
  return decoded.replace(/<\/?[^>]+(>|$)/g, '').trim();
}

function parseFeatureGridListItems(html?: string): string[] {
  if (!html) return [];
  const decoded = decodeHtmlEntities(html);
  const matches = decoded.match(/<li>([\s\S]*?)<\/li>/gi) || [];
  return matches.map((li) =>
    li
      .replace(/^<li>|<\/li>$/gi, '')
      .replace(/<\/?span[^>]*>/gi, '')
      .trim()
  );
}
export function getFeatureGridData(t: any) {
  const rawCards = t('feature_grid_cards', { returnObjects: true });

  if (Array.isArray(rawCards) && rawCards.length) {
    const rawButtons = t('feature_grid_buttons', { returnObjects: true });
    const buttons =
      (Array.isArray(rawButtons) && rawButtons[0]) || ({} as any);

    const cardsFinal: CardItem[] = rawCards.map((card: any) => ({
      id: card.id,
      logo: getDrupalImageUrl(card.image?.url || ''),
      title: stripFeatureGridWrapperTags(card.title),
      description: stripFeatureGridWrapperTags(card.description),
      listTitle: stripFeatureGridWrapperTags(card.listTitle),
      list: parseFeatureGridListItems(card.list)
    }));

    return {
      readButton: buttons.readMore || 'Read more',
      minimizeButton: stripLeadingDash(buttons.minimize) || 'Minimize',
      title: stripFeatureGridWrapperTags(t('feature_grid_title')),
      supportingText: stripFeatureGridWrapperTags(t('feature_grid_description')),
      cardsFinal
    };
  }
  return t('featureGrid', { returnObjects: true });
}

function renderListFromArray(
  listArray: any[] = [],
  maxItems?: number,
  totalItems?: number,
  splitColumns?: boolean,
  focusTenthLi?: boolean
): JSX.Element {
  const total = totalItems ?? listArray.length;
  const visibleCount = maxItems ?? listArray.length;
  const processedItems = listArray.map((item: string, idx: number) => {
    const count = idx + 1;
    const hidden = maxItems && count > visibleCount;
    const decodedItem = decodeHtmlEntities(item);
    const parts = decodedItem.split(/(<strong>.*?<\/strong>)/g);
    const segNodes = parts.filter(Boolean).map((part: string, sIdx: number) => {
      const strongMatch = part.match(/<strong>(.*?)<\/strong>/);
      if (strongMatch) {
        return <b key={sIdx}>{strongMatch[1]}</b>;
      }
      return <span key={sIdx}>{part}</span>;
    });
    const liProps: any = {
      key: idx,
      role: 'listitem',
      'aria-posinset': count,
      'aria-setsize': total,
      // tabIndex: focusTenthLi && idx === 9 ? -1 : undefined,
      id: focusTenthLi && idx === 9 ? 'tenth-li' : undefined,
      style: hidden ? { display: 'none' } : undefined,
      'aria-hidden': hidden ? 'true' : undefined
    };
    return (
      <li {...liProps}>
        {segNodes}
        <span
          style={{ position: 'absolute', left: -9999 }}
        >{`${count} of ${total}`}</span>
      </li>
    );
  });

  if (splitColumns) {
    const mid = Math.floor(processedItems.length / 2);
    const col1 = processedItems.slice(0, mid);
    const col2 = processedItems.slice(mid);
    return (
      <div className="featuregrid-two-column-wrapper" role="list">
        <ul className="featuregrid-two-column-left" role="presentation">
          {col1}
        </ul>
        <ul className="featuregrid-two-column-right" role="presentation">
          {col2}
        </ul>
      </div>
    );
  }

  return (
    <div>
      {maxItems && total > visibleCount && (
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{ position: 'absolute', left: -9999 }}
        >
          List with {total} items
        </div>
      )}
      <ul>{processedItems}</ul>
    </div>
  );
}

function DynamicAccordionLayout() {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [isAriaHiding, setIsAriaHiding] = useState(false);
  const prevExpandedRef = useRef<number | null>(null);

  // New: Focus Bridge Ref to hold focus briefly and avoid page-title announcement
  const focusBridgeRef = useRef<HTMLDivElement | null>(null);

  const [announcement, setAnnouncement] = useState('');
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const [lastTrigger, setLastTrigger] = useState<
    'readMore' | 'expandBtn' | null
  >(null);
  const lastTriggerRef = useRef<'readMore' | 'expandBtn' | null>(null); // new: synchronous trigger ref

  const readMoreBtnRef = useRef<(HTMLButtonElement | null)[]>([]);

  const headingRefs = useRef<(HTMLDivElement | null)[]>([]);
  const expandBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]); // new: refs to rendered content containers

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));


  const { t } = useTranslation();

  const json = getFeatureGridData(t);
  const data = json['cardsFinal'];

  const toggle = (id: number, trigger?: 'expandBtn' | 'readMore') => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Only focus heading when expanding, not collapsing
    const isExpanding = expanded !== id;

    // Use focus bridge to absorb screen reader's automatic announcement
    // focusBridgeRef.current?.focus();

    if (trigger === 'expandBtn' && isExpanding) {
      // then move focus to the intended heading after a short delay
      setTimeout(() => {
        headingRefs.current[id]?.focus();
      }, 500);
    }

    setTimeout(() => {
      prevExpandedRef.current = expanded;

      setExpanded((prev) => (prev === id ? null : id));

      if (trigger) {
        lastTriggerRef.current = trigger;
        setLastTrigger(trigger);
      }
    }, 300);
  };

  const removehTags = (str?: string): string => {
    if (!str) {
      return '';
    }
    return str;
  };

  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setAnnouncement(''), 100);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  // Manage the aria-hidden state with a delay when collapsing
  useEffect(() => {
    if (expanded === 0) {
      setIsAriaHiding(true);
    } else if (expanded === null && prevExpandedRef.current === 0) {
      // Keep hidden for 1 second after collapsing the first card
      const timer = setTimeout(() => {
        setIsAriaHiding(false);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setIsAriaHiding(false);
    }
  }, [expanded]);

  // When a card collapses, move focus to the trigger button
  useEffect(() => {
    // We check prevExpandedRef.current to know WHICH card just closed
    if (expanded === null && prevExpandedRef.current !== null) {
      const prevId = prevExpandedRef.current;
      const triggerAtCollapse = lastTriggerRef.current;

      const timer = setTimeout(() => {
        let focusTarget;
        if (prevId === 0 && triggerAtCollapse === 'readMore') {
          // Focus the trap instead of the button
          focusTarget = readMoreBtnRef.current[0];
        }
        if (focusTarget) {
          focusTarget.focus();
        } else {
          focusTarget = expandBtnRefs.current[prevId];
          if (focusTarget && !focusTarget.hasAttribute('disabled')) {
            focusTarget.focus();
          }
        }
      }, 700);

      lastTriggerRef.current = null;
      setLastTrigger(null);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [expanded]);

  useEffect(() => {
    // Only run if a card is expanded (not the first), and the last trigger was expandBtn
    if (
      expanded !== null &&
      expanded !== 0 &&
      lastTriggerRef.current === 'expandBtn'
    ) {
      // Wait for DOM update
      setTimeout(() => {
        // Focus the content container
        contentRefs.current[expanded]?.focus();
      }, 700);
    }
  }, [expanded]);

  // Determine slide direction based on previous and current expanded
  const getSlideClass = (itemId: number, index: number) => {
    const prevExpanded = prevExpandedRef.current;

    // Only animate non-expanded cards
    if (itemId === expanded) return '';

    // If no previous expanded or same as current, no animation
    if (prevExpanded === null) return '';

    if (expanded === null) return '';

    let direction = '';

    if (expanded === 0) {
      // First card is now expanded - others slide LEFT
      direction = 'slide-left';
    } else if (prevExpanded === 0 && expanded !== 0) {
      // Was first card, now another - others slide RIGHT
      direction = 'slide-right';
    } else if (prevExpanded !== 0 && expanded !== 0 && prevExpanded !== null) {
      // Switching between non-first cards - no horizontal shift needed
      direction = '';
    } else if (prevExpanded === null && expanded !== 0) {
      // From collapsed to non-first expanded - slide RIGHT
      direction = 'slide-right';
    }

    return `${direction} slide-delay-${index}`;
  };

  const AccordionItem = ({ item, isExpanded = false }: any) => {
    const anyExpanded = expanded !== null;
    const shouldHideFromA11y = isAriaHiding && item.id !== 0;

    const previewText = item.description || '';

    const showReadMoreButton =
      item.id === 0 && expanded === null && !isMobile && !isTablet;

    // Process content for first card / other cards using list arrays and description
    let processedContentJSX: JSX.Element | null = null;
    if (item.id !== 0) {
      const liCount = (item.list || []).length;
      // only build the list here; render description inside AccordionDetails via Typography
      processedContentJSX = renderListFromArray(
        item.list || [],
        undefined,
        liCount || undefined,
        false,
        true
      );
    }

    if (item.id === 0 && (isMobile || isTablet) && !isExpanded) {
      processedContentJSX = <div>{previewText}</div>;
    } else if (item.id === 0 && (isMobile || isTablet) && isExpanded) {
      // render only the list here; description is rendered directly in AccordionDetails
      processedContentJSX = <div>{renderListFromArray(item.list || [])}</div>;
    } else if (item.id === 0 && expanded === null && !isMobile && !isTablet) {
      processedContentJSX = (
        <div>
          {renderListFromArray(
            item.list || [],
            9,
            (item.list || []).length,
            false,
            true
          )}
        </div>
      );
    } else if (item.id === 0 && isExpanded && !isMobile && !isTablet) {
      processedContentJSX = (
        <div>
          {renderListFromArray(
            item.list || [],
            undefined,
            (item.list || []).length,
            true,
            true
          )}
        </div>
      );
    }

    const applyHover = !isMobile && !isTablet && expanded !== item.id;

    return (
      <Accordion
        disableGutters
        square
        expanded={isExpanded}
        onChange={() => {}}
        aria-hidden={shouldHideFromA11y ? 'true' : undefined}
        sx={{
          backgroundColor: item.id === 0 ? '#1460AA' : 'auto',
          color: item.id === 0 ? 'white' : 'auto',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 0 15px 0 rgba(0,0,0,0.15)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: isExpanded ? 3 : 1,
          transition: 'box-shadow 3s ease',
          '&:hover': applyHover
            ? {
                transform: 'scale(1.02)',
                boxShadow: '0px -5px 15px 0px rgba(0,0,0,0.15)',
                '& .expand-icon-hover': {
                  transform: 'scale(1.3)'
                }
              }
            : {},
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            minHeight: 0,
            padding: 0,
            '&.Mui-expanded': { minHeight: 0 }
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            '&.Mui-expanded': { margin: 0 }
          },
          '& .MuiAccordionDetails-root': {
            padding: 0,
            marginTop: 0
          }
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: isMobile ? '16px' : '24px',
            marginBottom:
              isMobile && item.id === 0
                ? '40px'
                : isMobile && item.id === 0
                  ? '16px'
                  : expanded !== null
                    ? '24px'
                    : '12px',
            flex: 1,
            cursor: expanded !== item.id ? 'pointer' : 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (expanded !== item.id) toggle(item.id);
          }}
        >
          {((isTablet || isMobile) && expanded !== null
            ? null
            : expanded !== item.id) && (
            <button
              ref={(el) => (expandBtnRefs.current[item.id] = el)}
              style={{
                width: '20px',
                height: '20px',
                background: 'transparent',
                border: 'none',
                alignSelf: 'end',
                padding: 0,
                cursor: 'pointer'
              }}
              aria-label={removehTags(item.title)}
              className="expand-icon-hover"
              aria-expanded={expanded === item.id}
              onClick={(e) => {
                e.stopPropagation();
                lastTriggerRef.current = 'expandBtn';
                setLastTrigger('expandBtn');
                toggle(item.id, 'expandBtn');
                setTimeout(() => {
                  const heading = headingRefs.current[item.id];
                  if (heading) {
                    heading?.focus();
                    heading.blur();
                    heading.focus();
                  }
                }, 500);
              }}
            >
              <img
                src={item.id === 0 ? ExpandIcon : Vector}
                alt=""
                // onClick={(e) => {
                //   e.stopPropagation();
                //   lastTriggerRef.current = 'expandBtn';
                //   setLastTrigger('expandBtn');
                //   toggle(item.id, 'expandBtn');
                // }}
                style={{
                  alignSelf: 'end',
                  height: '16px',
                  width: '16px'
                }}
              />
            </button>
          )}
          {isMobile && expanded === item.id ? (
            <img
              src={closeIcon}
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(item.id);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' || e.key === ' ') {
                  toggle(item.id);
                }
              }}
              tabIndex={0}
              aria-label="close"
              style={{
                alignSelf: 'end',
                height: '32px',
                width: '42px',
                objectFit: 'contain',
                cursor: 'pointer'
              }}
            />
          ) : null}
          {expanded !== null && expanded !== item.id ? (
            <Box
              sx={{
                display: isExpanded ? 'block' : 'flex'
              }}
            >
              <img
                aria-hidden="true"
                src={item.logo}
                alt=""
                style={{
                  alignSelf: 'start',
                  height: isMobile ? '32px' : '40px',
                  width: isMobile ? '32px' : '40px'
                }}
              />
              <AccordionSummary
                ref={(el) => (headingRefs.current[item.id] = el)}
                onClick={() => toggle(item.id)}
                sx={{ pointerEvents: 'none' }}
                tabIndex={-1}
                role="presentation"
                aria-live={isExpanded ? 'polite' : undefined}
              >
                <Typography
                  component="h2"
                  variant="h2"
                  sx={{
                    pl: '20px',
                    color: item.id === 0 ? 'white' : 'black',
                    fontSize: isTablet
                      ? '18px !important'
                      : isMobile
                        ? '14px !important'
                        : '20px !important',
                    fontWeight: isTablet ? '500 !important' : '700 !important',
                    lineHeight: isTablet
                      ? '24px !important'
                      : isMobile
                        ? '20px !important'
                        : '28px !important',
                    mb: 0,
                    pt: 0
                  }}
                >
                  {item.title}
                </Typography>
              </AccordionSummary>
            </Box>
          ) : (
            <>
              <img
                aria-hidden="true"
                alt=""
                src={item.logo}
                style={{
                  alignSelf: 'start',
                  height: isMobile ? '32px' : '40px',
                  width: isMobile ? '32px' : '40px'
                }}
              />
              <AccordionSummary
                ref={(el) => (headingRefs.current[item.id] = el)}
                onClick={() => toggle(item.id)}
                sx={{ pointerEvents: 'none' }}
                tabIndex={-1}
                role="presentation"
              >
                <Typography
                  component="h2"
                  variant="h2"
                  sx={{
                    marginBottom: 0,
                    marginTop: isMobile ? '12px' : '26px',
                    color: item.id === 0 ? 'white' : 'auto',
                    fontSize: isTablet
                      ? '18px !important'
                      : isMobile
                        ? '14px !important'
                        : '20px !important',
                    fontWeight: isTablet ? '500 !important' : '700 !important',
                    lineHeight: isTablet
                      ? '24px !important'
                      : isMobile
                        ? '20px !important'
                        : '28px !important'
                  }}
                >
                  {item.title}
                </Typography>
              </AccordionSummary>
            </>
          )}

          {!anyExpanded ? (
            isExpanded ? (
              <AccordionDetails sx={{ p: 0, mt: 0 }}>
                {item.description && (
                  <Typography
                    component="div"
                    sx={{
                      py: '14px',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: 400,
                      lineHeight: isMobile ? '16px' : '20px'
                    }}
                  >
                    {item.description}
                  </Typography>
                )}
                {item.listTitle && processedContentJSX && (
                  <Typography
                    component="div"
                    sx={{ mb: '8px', fontWeight: 700 }}
                  >
                    {item.listTitle}
                  </Typography>
                )}
                <Typography
                  component="p"
                  ref={(el) => (contentRefs.current[item.id] = el)} // new: store content container ref
                  sx={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 400,
                    lineHeight: isMobile ? '16px' : '20px',
                    '& ul': {
                      pl: '30px'
                    },
                    '& li': {
                      pb: '7px'
                    }
                  }}
                >
                  {processedContentJSX}
                </Typography>
              </AccordionDetails>
            ) : (
              previewText && (
                <Box sx={{ my: 2 }}>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: 400,
                      lineHeight: isMobile ? '16px' : '20px'
                    }}
                  >
                    {previewText}
                  </Typography>
                </Box>
              )
            )
          ) : isExpanded ? (
            <AccordionDetails sx={{ p: 0, mt: 0 }}>
              {item.description && (
                <Typography
                  component="div"
                  sx={{
                    py: '14px',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 400,
                    lineHeight: isMobile ? '16px' : '20px'
                  }}
                >
                  {item.description}
                </Typography>
              )}
              {item.listTitle && processedContentJSX && (
                <Typography component="div" sx={{ mb: '8px', fontWeight: 700 }}>
                  {item.listTitle}
                </Typography>
              )}
              <Typography
                component="div"
                ref={(el) => (contentRefs.current[item.id] = el)} // new: store content container ref
                sx={{
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: 400,
                  lineHeight: isMobile ? '16px' : '20px',
                  '& ul': {
                    pl: '30px'
                  },
                  '& li': {
                    pb: '8px'
                  }
                }}
              >
                {processedContentJSX}
              </Typography>
            </AccordionDetails>
          ) : null}
        </div>
        <Box
          sx={{
            p: !isMobile ? '24px' : 0,
            pt: 0,
            cursor: expanded !== item.id ? 'pointer' : 'auto'
          }}
          onClick={(e) => {
            if (expanded !== item.id) {
              toggle(item.id);
            }
          }}
        >
          {item.id === 0 && showReadMoreButton && (
            <Button
              ref={(el) => (readMoreBtnRef.current[item.id] = el)}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                lastTriggerRef.current = 'readMore';
                setLastTrigger('readMore');
                toggle(item.id, 'readMore');
              }}
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '20px',
                background: 'transparent',
                p: '10px',
                width: '146px',
                borderRadius: '24px',
                color: '#1460AA',
                '&:hover': {
                  background: 'transparent',
                  boxShadow: 'none',
                  color: '#1460AA',
                  textDecoration: 'none'
                },
                '&:focus-visible': {
                  outline: '2px solid black',
                  outlineOffset: '-4px'
                }
              }}
              tabIndex={0}
              aria-expanded={expanded === 0 ? true : false}
              aria-label={json['readButton']}
            >
              {/* <span
                aria-hidden="true"
                tabIndex={-1}
                ref={el => (readMoreBtnRef.current['trap'] = el)}
                onKeyDown={(e) => {
                  // when trap (programmatically focused) receives Shift+Tab, move focus to first card expand icon
                  if (e.shiftKey && e.key === 'Tab') {
                    e.preventDefault();
                    const firstExpand = expandBtnRefs.current[0];
                    if (firstExpand && typeof firstExpand.focus === 'function') {
                      firstExpand.focus();
                    }
                  }
                }}
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                  padding: 0,
                  margin: 0,
                  outline: 'none'
                }}
                role="button"
              >collapsed Read more</span> */}
              {json['readButton']}
            </Button>
          )}
          {expanded === item.id && !isMobile && (
            <Button
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                // Ensure we pass the trigger type so the useEffect knows where to return focus
                const trig =
                  item.id === 0 && lastTrigger === 'readMore'
                    ? 'readMore'
                    : 'expandBtn';
                setTimeout(() => {
                  toggle(item.id, trig);
                }, 50);
              }}
              aria-expanded={expanded !== null ? true : false}
              aria-label={`${json['minimizeButton']} ${removehTags(item.title)}`}
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '20px',
                background: 'transparent',
                boxShadow: 'none',
                color: item.id === 0 ? 'white' : 'auto',
                minWidth: '92px',
                '&:hover': {
                  background: 'transparent',
                  boxShadow: 'none',
                  color: item.id === 0 ? 'white' : '#1460AA',
                  textDecoration: 'none'
                },
                '&:focus-visible': {
                  outline: '2px solid black',
                  outlineOffset: '-4px'
                },
                justifyContent: 'left',
                pl: 0
              }}
            >
              - {json['minimizeButton']}
            </Button>
          )}
        </Box>
      </Accordion>
    );
  };

  // COLLAPSED GRID (render dynamically to avoid indexing missing items)
  const renderCollapsed = () => {
    const prevExpanded = prevExpandedRef.current;
    // Mobile layout: single column
    if (isMobile) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'all 1s ease-in-out'
          }}
        >
          {data.map((itm, idx) => (
            <Box
              sx={{
                minWidth: '344px',
                minHeight: idx === 4 ? '156px' : '176px'
              }}
              className={
                prevExpanded === 0
                  ? 'mobile-collapse'
                  : prevExpanded === itm.id && expanded === null
                    ? 'mobile-other-collapse'
                    : ''
              }
            >
              <AccordionItem key={itm.id} item={itm} isExpanded={false} />
            </Box>
          ))}
        </Box>
      );
    }
    // Tablet layout: first card spans full width (3 columns), remaining cards in 2 rows below
    if (isTablet) {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            transition: 'all 0.5s ease-in-out'
          }}
        >
          {/* First card spans all 3 columns */}
          <Box
            sx={{ gridColumn: '1 / -1', minHeight: '256px' }}
            className="tablet-collapse"
          >
            <AccordionItem item={data[0]} isExpanded={false} />
          </Box>

          {/* Remaining cards (2-5) in rows below */}
          {data.slice(1).map((itm, idx) => (
            <Box key={itm.id} sx={{ minHeight: '262px' }}>
              <AccordionItem item={itm} isExpanded={false} />
            </Box>
          ))}
        </Box>
      );
    }

    //For Desktop
    const remainingCount = Math.max(0, data.length - 1);
    const rowsCount = Math.max(2, Math.ceil(remainingCount / 2)); // ensure at least 2 rows so first spans both
    return (
      <Box
        sx={{
          minHeight: '602px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', // first col (big), second & third for remaining cards
          gridTemplateRows: `repeat(${rowsCount}, auto)`,
          gap: 2,
          columnGap: '18px',
          transition: 'all 1s ease-in-out'
        }}
      >
        {data.map((itm, idx) => {
          if (idx === 0) {
            // first card spans all rows in column 1
            return (
              <Box
                key={itm.id}
                sx={{ gridColumn: '1', gridRow: `1 / span ${rowsCount}` }}
                className={prevExpanded === 0 ? 'desktopFirstCollapse' : ''}
              >
                <AccordionItem item={itm} isExpanded={true} />
              </Box>
            );
          }
          const j = idx - 1;
          const col = 2 + (j % 2);
          const row = Math.floor(j / 2) + 1;
          return (
            <Box
              key={itm.id}
              sx={{
                maxWidth: '388px',
                minHeight: '293px',
                gridColumn: `${col}`,
                gridRow: `${row}`,
                transition: 'all 1s ease-in-out'
              }}
              className={
                prevExpanded === 0
                  ? `slide-in-from-right slide-delay-${j}`
                  : 'desktopOtherExpand'
              }
            >
              <AccordionItem item={itm} isExpanded={false} />
            </Box>
          );
        })}
      </Box>
    );
  };

  // EXPANDED MODE with CSS class animations
  const renderExpanded = () => {
    const expandedItem = data.find((d) => d.id === expanded);
    const others = data.filter((d) => d.id !== expanded);

    // Mobile layout: single column with expanded card first
    if (isMobile) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            transition: 'all 1s ease-in-out'
          }}
        >
          {data.map((itm) => (
            <Box
              key={itm.id}
              className={
                itm.id === 0 && expanded === 0
                  ? 'mobile-first-expanded'
                  : itm.id === expanded && expanded !== 0
                    ? 'mobile-expanded'
                    : ''
              }
            >
              <AccordionItem item={itm} isExpanded={itm.id === expanded} />
            </Box>
          ))}
        </Box>
      );
    }

    // Tablet Layout
    if (isTablet) {
      const expandedIdx = data.findIndex((d) => d.id === expanded);

      if (expandedIdx === 0) {
        // First card expanded - takes complete first row, remaining 4 cards in 2 rows with 2 columns each
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2
            }}
          >
            {/* Row 1: First card expanded, spans both columns */}
            <Box
              sx={{ gridColumn: '1 / -1' }}
              className="tablet-first-expanded"
            >
              <AccordionItem item={expandedItem} isExpanded />
            </Box>

            {/* Row 2: Cards 1, 2 */}
            <AccordionItem item={data[1]} />
            <AccordionItem item={data[2]} />

            {/* Row 3: Cards 3, 4 */}
            <AccordionItem item={data[3]} />
            <AccordionItem item={data[4]} />
          </Box>
        );
      } else if (expandedIdx === 1 || expandedIdx === 2) {
        // 2nd or 3rd card expanded
        const otherInFirstRow = expandedIdx === 1 ? data[2] : data[1];
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              transition: 'all 1s ease-in-out'
            }}
          >
            {/* Row 1: Card 0 and the non-expanded card (1 or 2) */}
            <AccordionItem aria-hidden="true" item={data[0]} />
            <AccordionItem aria-hidden="true" item={otherInFirstRow} />

            {/* Row 2: Expanded card takes full width */}
            <Box
              sx={{ gridColumn: '1 / -1' }}
              className={expanded === null ? '' : 'tablet-other-card-expand'}
            >
              <AccordionItem item={expandedItem} isExpanded />
            </Box>

            {/* Row 3: Cards 3, 4 */}
            <AccordionItem aria-hidden="true" item={data[3]} />
            <AccordionItem aria-hidden="true" item={data[4]} />
          </Box>
        );
      } else if (expandedIdx === 3 || expandedIdx === 4) {
        // 4th or 5th card expanded
        const otherInThirdRow = expandedIdx === 3 ? data[4] : data[3];
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2,
              transition: 'all 1s ease-in-out'
            }}
          >
            {/* Row 1: Cards 0, 1 */}
            <AccordionItem aria-hidden="true" item={data[0]} />
            <AccordionItem aria-hidden="true" item={data[1]} />

            {/* Row 2: Cards 2, and the non-expanded card (3 or 4) */}
            <AccordionItem aria-hidden="true" item={data[2]} />
            <AccordionItem item={otherInThirdRow} />

            {/* Row 3: Expanded card takes full width */}
            <Box
              sx={{ gridColumn: '1 / -1' }}
              className={expanded === null ? '' : 'tablet-other-card-expand'}
            >
              <AccordionItem item={expandedItem} isExpanded />
            </Box>
          </Box>
        );
      }
    }

    // Desktop layout with CSS class animations
    const bigLeft = expanded === 0;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: bigLeft ? '2fr 1fr' : '1fr 2fr',
          gap: 2,
          transition: 'grid-template-columns 0.3s ease-in-out',
          position: 'relative',
          alignItems: 'stretch'
        }}
      >
        {bigLeft && expandedItem && (
          <Box sx={{ zIndex: 1 }} className="desktopFirst">
            <AccordionItem item={expandedItem} isExpanded />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            zIndex: 2,
            height: '100%'
          }}
        >
          {others.map((item, index) => (
            <div
              key={item.id}
              className={getSlideClass(item.id, index)}
              style={{
                position: 'relative',
                zIndex: 10 + index,
                flex: 1,
                display: 'flex'
              }}
            >
              <AccordionItem item={item} />
            </div>
          ))}
        </Box>

        {!bigLeft && expandedItem && (
          <Box
            sx={{ zIndex: 1, transition: 'all 1s ease-in-out' }}
            className="desktopOtherExpand"
          >
            <AccordionItem item={expandedItem} isExpanded />
          </Box>
        )}
      </Box>
    );
  };

  // Add this useEffect to announce when focus is missing after expansion
  useEffect(() => {
    if (expanded === 0 && lastTriggerRef.current === 'readMore') {
      const interval = setInterval(() => {
        const tenthLi = document.getElementById('tenth-li');
        if (tenthLi) {
          (tenthLi as HTMLElement).focus();
          const tenthLiText = tenthLi.textContent?.trim() || 'item 10';
          // Set announcement AFTER a small delay to ensure live region picks it up
          setTimeout(() => {
            setAnnouncement(`expanded ${tenthLiText}`);
          }, 200);
          clearInterval(interval); // Stop checking once the element is found
        }
      }, 50); // Check every 50ms

      return () => clearInterval(interval); // Cleanup interval on unmount
    }
  }, [expanded]);

  return (
    <>
      {/* Focus Bridge: Holds focus during DOM unmounting to prevent title announcement */}
      <div
        ref={focusBridgeRef}
        tabIndex={-1}
        aria-hidden="true"
        aria-label="expanding"
        style={{
          position: 'absolute',
          left: -9999,
          width: 1,
          height: 1,
          overflow: 'hidden'
        }}
      >
        &nbsp;
      </div>

      {/* Visually Hidden Live Region */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          height: '1px',
          width: '1px',
          margin: '-1px',
          border: 0,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)'
        }}
      >
        {announcement}
      </div>

      {expanded !== null ? renderExpanded() : renderCollapsed()}
    </>
  );
}

function FeatureDesktopLayout() {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const lastClickedIndexRef = useRef<number | null>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const announcementTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const focusRefs = useRef<{
    [key: string | number]: HTMLButtonElement | null;
  }>({});
  const titleRefs = useRef<{
    [key: string | number]: HTMLHeadingElement | null;
  }>({});
  const tenthListItemRef = useRef<HTMLLIElement | null>(null);

  const data = getFeatureGridData(t);
  const rawCardsData = data['cardsFinal'] || [];
  const cardsData: CardItem[] = Array.isArray(rawCardsData[1])
    ? [rawCardsData[0], ...rawCardsData[1]]
    : rawCardsData;
  const readButtonLabel = data['readButton'] || 'Read more';
  const minimizeButtonLabel = data['minimizeButton'] || 'Minimize';

  const handleBoxClick = (index: number) => {
    const newExpandedIndex = expandedIndex === index ? null : index;
    const isCollapsing = expandedIndex === index && newExpandedIndex === null;

    if (isCollapsing && liveRegionRef.current) {
      const collapseCard = cardsData[index];
      const collapseAnnouncement = `${collapseCard.title} collapsed.`;
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = null;
      }
      liveRegionRef.current.setAttribute('aria-live', 'assertive');
      liveRegionRef.current.textContent = collapseAnnouncement;
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.setAttribute('aria-live', 'polite');
        }
      }, 100);
    }

    // Small delay to ensure blur takes effect before state change
    setTimeout(() => {
      lastClickedIndexRef.current = index;
      setExpandedIndex(newExpandedIndex);
    }, 50);
  };

  useEffect(() => {
    if (lastClickedIndexRef.current !== null) {
      const index = lastClickedIndexRef.current;
      const wasExpanded = expandedIndex === index;

      if (wasExpanded) {
        if (index === 0 && tenthListItemRef.current) {
          tenthListItemRef.current.focus();
        } else {
          const title = titleRefs.current[index];
          if (title) {
            title.focus();
          }
        }
      } else {
        const focusedButton = focusRefs.current[index];
        if (focusedButton) {
          focusedButton.focus();
        }
      }
      lastClickedIndexRef.current = null;
    }
  }, [expandedIndex]);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBoxClick(index);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (expandedIndex !== index) {
        lastClickedIndexRef.current = index;
        setExpandedIndex(index);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (expandedIndex === index) {
        if (liveRegionRef.current) {
          const collapseCard = cardsData[index];
          const collapseAnnouncement = `${collapseCard.title} collapsed.`;
          if (announcementTimeoutRef.current) {
            clearTimeout(announcementTimeoutRef.current);
            announcementTimeoutRef.current = null;
          }
          liveRegionRef.current.setAttribute('aria-live', 'assertive');
          liveRegionRef.current.textContent = collapseAnnouncement;
          setTimeout(() => {
            if (liveRegionRef.current) {
              liveRegionRef.current.setAttribute('aria-live', 'polite');
            }
          }, 100);
        }
        lastClickedIndexRef.current = index;
        setExpandedIndex(null);
      }
    }
  };

  const handleMinimizeKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (liveRegionRef.current) {
        const collapseCard = cardsData[index];
        const collapseAnnouncement = `${collapseCard.title} collapsed.`;
        if (announcementTimeoutRef.current) {
          clearTimeout(announcementTimeoutRef.current);
          announcementTimeoutRef.current = null;
        }
        liveRegionRef.current.setAttribute('aria-live', 'assertive');
        liveRegionRef.current.textContent = collapseAnnouncement;
        setTimeout(() => {
          if (liveRegionRef.current) {
            liveRegionRef.current.setAttribute('aria-live', 'polite');
          }
        }, 100);
      }
      lastClickedIndexRef.current = index;
      setExpandedIndex(null);
    }
  };

  const parseStrongText = (text: string, color: string = '#000000') => {
    const decodedText = decodeHtmlEntities(text);
    const parts = decodedText.split(/(<strong>.*?<\/strong>)/g);
    return parts.filter(Boolean).map((part, index) => {
      const strongMatch = part.match(/<strong>(.*?)<\/strong>/);
      if (strongMatch) {
        return (
          <strong key={index} style={{ color }}>
            {strongMatch[1]}
          </strong>
        );
      }
      return (
        <span key={index} style={{ color }}>
          {part}
        </span>
      );
    });
  };

  const renderFeatureBox = (
    card: CardItem,
    index: number,
    isExpanded: boolean
  ) => {
    const isMainBox = index === 0;
    const isOtherItemExpanded = expandedIndex !== null && expandedIndex !== 0;
    const descriptionId = `feature-desc-${index}`;
    const titleId = `feature-title-${index}`;
    const listId = `feature-list-${index}`;
    const buttonId = `feature-button-${index}`;
    const buttonRef = (el: HTMLButtonElement | null) => {
      if (el) focusRefs.current[index] = el;
    };
    const iconButtonRef = (el: HTMLButtonElement | null) => {
      if (el) focusRefs.current[`icon-${index}`] = el;
    };
    const titleRef = (el: HTMLHeadingElement | null) => {
      if (el) titleRefs.current[index] = el;
    };
    const shouldShowList = isMainBox ? !isOtherItemExpanded : isExpanded;

    return (
      <Box
        ref={buttonRef}
        className={`feature-box ${isExpanded ? 'expanded' : ''}`}
        onClick={() => handleBoxClick(index)}
        onKeyDown={(e) => handleKeyDown(e, index)}
        tabIndex={0}
        sx={{
          width:
            expandedIndex === null && !isMainBox
              ? '100%'
              : expandedIndex !== null && !isExpanded
                ? '410px'
                : isExpanded
                  ? '774px'
                  : '388px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent:
            expandedIndex !== null && !isExpanded
              ? 'space-between'
              : 'flex-start',
          alignItems: 'flex-start',
          padding:
            expandedIndex !== null && !isExpanded
              ? '16px 10px 16px 16px'
              : isMainBox
                ? '22px 24px 24px 24px'
                : '22px 22px 22px 22px',
          minWidth: expandedIndex === null && !isMainBox ? '0' : 'auto',
          minHeight:
            expandedIndex !== null && !isExpanded
              ? '138.5px'
              : isExpanded
                ? '602px'
                : isMainBox
                  ? '602px'
                  : '293px',
          backgroundColor: isMainBox ? '#1460AA' : '#ffffff',
          borderRadius: '16px',
          border: '0px',
          opacity: 1,
          transition: 'all 0.3s ease-in-out',
          boxShadow: '0px 0px 15px 0px #00000026',
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: '0px 0px 15px 0px #00000066',
            backgroundColor: isMainBox ? '#1460AA' : '#ffffff',
            textDecoration: 'none',
            '& button img': {
              width: '22px',
              height: '22px',
              objectFit: 'contain'
            }
          },
          '&:focus-visible': {
            outline: '3px solid #0066cc',
            outlineOffset: '2px'
          },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      >
        {expandedIndex !== null && !isExpanded ? (
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                width: '100%',
                marginBottom: '0.5rem'
              }}
            >
              <Button
                ref={iconButtonRef}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBoxClick(index);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  handleKeyDown(e, index);
                }}
                aria-label={`Expand ${card.title} details`}
                title={`Expand ${card.title} details`}
                sx={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:focus-visible': {
                    outline: '2px solid #0066cc',
                    outlineOffset: '2px',
                    borderRadius: '4px'
                  }
                }}
              >
                <Box
                  component="img"
                  src={isMainBox ? ExpandIcon : Vector}
                  alt=""
                  sx={{ width: '16px', height: '16px', objectFit: 'contain' }}
                />
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                width: '100%'
              }}
            >
              <Box
                component="img"
                src={card.logo}
                alt={`${card.title} feature icon`}
                sx={{
                  width: '40px',
                  height: '40px',
                  rotate: '0deg',
                  opacity: 1,
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
              <h3
                className="feature-title"
                id={titleId}
                ref={titleRef}
                tabIndex={-1}
                style={{
                  margin: 0,
                  fontFamily: 'AC Nord Text, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: isMainBox ? '#ffffff' : '#000000'
                }}
              >
                {card.title}
              </h3>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column-reverse',
              alignItems: 'flex-start',
              width: '100%',
              marginBottom:
                expandedIndex !== null && !isExpanded ? '0' : '1.5rem'
            }}
          >
            <Box
              component="img"
              src={card.logo}
              alt={`${card.title} feature icon`}
              sx={{
                width: '40px',
                height: '40px',
                rotate: '0deg',
                opacity: 1,
                objectFit: 'contain'
              }}
            />
            {!isExpanded && (
              <Box
                sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}
              >
                <Button
                  ref={iconButtonRef}
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBoxClick(index);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleKeyDown(e, index);
                  }}
                  aria-label={`Expand ${card.title} details`}
                  title={`Expand ${card.title} details`}
                  sx={{
                    minWidth: '44px',
                    minHeight: '44px',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:focus-visible': {
                      outline: '2px solid #0066cc',
                      outlineOffset: '2px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={isMainBox ? ExpandIcon : Vector}
                    alt=""
                    aria-hidden="true"
                    sx={{ width: '16px', height: '16px', objectFit: 'contain' }}
                  />
                </Button>
              </Box>
            )}
          </Box>
        )}

        {!(expandedIndex !== null && !isExpanded) && (
          <Box
            className="feature-content"
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: expandedIndex !== null && !isExpanded ? '0' : '0.5',
              flex: expandedIndex !== null && !isExpanded ? 0 : 1,
              overflow: 'hidden'
            }}
          >
            <h3
              className="feature-title"
              id={titleId}
              ref={titleRef}
              tabIndex={-1}
              style={{
                margin: 0,
                fontFamily: 'AC Nord Text, sans-serif',
                fontWeight: 700,
                fontSize: '20px',
                lineHeight: '28px',
                letterSpacing: '0px',
                color: isMainBox ? '#ffffff' : '#000000'
              }}
            >
              {card.title}
            </h3>
            <p
              className="feature-description"
              id={descriptionId}
              style={{
                margin: 0,
                fontFamily: 'AC Nord Text, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0px',
                verticalAlign: 'middle',
                color: isMainBox ? '#ffffff' : '#000000',
                display:
                  expandedIndex !== null && !isExpanded ? 'none' : 'block'
              }}
            >
              {card.description}
            </p>
            {shouldShowList && (
              <Box sx={{ marginTop: '1rem' }}>
                <h4
                  style={{
                    margin: '0 0 0.5rem 0',
                    fontFamily: 'AC Nord Text, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '1.5',
                    letterSpacing: '0px',
                    verticalAlign: 'middle',
                    color: isMainBox ? '#ffffff' : '#000000'
                  }}
                >
                  {card.listTitle}
                </h4>
                {!isExpanded && card.list.length > 9 && (
                  <div
                    aria-live="polite"
                    aria-atomic="true"
                    style={{ position: 'absolute', left: '-9999px' }}
                  >
                    List with {card.list.length} items
                  </div>
                )}
                <ul
                  id={listId}
                  role={isExpanded ? 'list' : 'presentation'}
                  style={{
                    margin: '0.5rem 0 0 1.5rem',
                    padding: 0,
                    listStyle: 'disc',
                    color: isMainBox ? '#ffffff' : '#000000',
                    columns: isMainBox && isExpanded ? 2 : 1,
                    columnGap: isMainBox && isExpanded ? '2rem' : 0
                  }}
                >
                  {card.list.map((item, itemIndex) => {
                    const totalItems = card.list.length;
                    const itemNumber = itemIndex + 1;
                    const isHidden = !isExpanded && itemIndex >= 9;
                    return (
                      <li
                        key={itemIndex}
                        ref={
                          isMainBox && itemIndex === 9
                            ? tenthListItemRef
                            : undefined
                        }
                        tabIndex={isMainBox && itemIndex === 9 ? -1 : undefined}
                        aria-posinset={itemNumber}
                        aria-setsize={totalItems}
                        aria-hidden={isHidden ? 'true' : undefined}
                        style={
                          isHidden
                            ? { display: 'none' }
                            : {
                                marginBottom: '0.5rem',
                                fontFamily: 'AC Nord Text, sans-serif',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '1.5',
                                letterSpacing: '0px',
                                verticalAlign: 'middle',
                                color: isMainBox ? '#ffffff' : '#000000'
                              }
                        }
                      >
                        {parseStrongText(
                          item,
                          isMainBox ? '#ffffff' : '#000000'
                        )}
                        <span
                          style={{
                            position: 'absolute',
                            left: '-10000px',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden'
                          }}
                        >
                          {`, ${itemNumber} of ${totalItems}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            marginTop: expandedIndex !== null && !isExpanded ? '0' : '2rem',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {isMainBox && !isExpanded && expandedIndex === null && (
            <Button
              id={buttonId}
              variant="primary"
              size="md"
              sx={{
                backgroundColor: 'white',
                color: '#1460AA',
                '&:hover': {
                  backgroundColor: 'white',
                  color: '#1460AA'
                }
              }}
            >
              {readButtonLabel}
            </Button>
          )}
          {isExpanded && (
            <Button
              onClick={() => handleBoxClick(index)}
              onKeyDown={(e) => handleMinimizeKeyDown(e, index)}
              aria-label={`Minimize ${card.title}`}
              sx={{
                minWidth: '44px',
                minHeight: '44px',
                padding: '8px 12px',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '20px',
                gap: '0.5rem',
                fontSize: '16px',
                fontWeight: 700,
                color: isMainBox ? '#ffffff' : '#004999',
                '&:hover': {
                  color: isMainBox ? '#ffffff' : '#004999',
                  textDecoration: 'none'
                },
                '&:focus-visible': {
                  outline: '2px solid ' + (isMainBox ? '#ffffff' : '#0066cc'),
                  outlineOffset: '2px',
                  borderRadius: '4px'
                }
              }}
            >
              - {minimizeButtonLabel}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const isMainExpanded = expandedIndex === 0;
  const isOtherExpanded = expandedIndex !== null && expandedIndex !== 0;
  const noExpanded = expandedIndex === null;

  if (cardsData.length !== 5) {
    console.warn('FeatureDesktopLayout expects exactly 5 items');
    return null;
  }

  return (
    <section
      className="feature-test-section"
      aria-label="Featured benefits grid"
      onBlur={() => {}}
    >
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="off"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      />
      <Grid
        container
        spacing={2}
        className="feature-test-container"
        sx={{ height: '100%', display: 'flex', width: '100%' }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={isMainExpanded ? 8 : 4}
          className={isOtherExpanded ? '' : 'grid-item-left'}
        >
          {noExpanded && (
            <div className="grid-wrapper-main">
              {renderFeatureBox(cardsData[0], 0, false)}
            </div>
          )}
          {isMainExpanded && (
            <div className="grid-wrapper-main">
              {renderFeatureBox(cardsData[0], 0, true)}
            </div>
          )}
          {isOtherExpanded && (
            <Grid container spacing={2} direction="column" sx={{
              '& > .MuiGrid-item': {
            paddingLeft: 0,
            }
            }}>
              <Grid item xs={12} className="grid-item">
                {renderFeatureBox(cardsData[0], 0, false)}
              </Grid>
              {cardsData.map((card, index) => {
                if (index === expandedIndex || index === 0) return null;
                return (
                  <Grid
                    item
                    xs={12}
                    key={`feature-${index}`}
                    className="grid-item"
                  >
                    {renderFeatureBox(card, index, false)}
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={isOtherExpanded ? 8 : isMainExpanded ? 4 : 8}
          className={isMainExpanded ? 'grid-item-right' : 'not-expanded'}
          role="group"
          aria-label="Features panel"
          sx={{ height: isMainExpanded || isOtherExpanded ? '100%' : 'auto' }}
        >
          {noExpanded && (
            <Grid container gap={2} sx={{ width: '100%', height: '100%' }}>
              {cardsData.slice(1).map((card, index) => {
                const actualIndex = index + 1;
                return (
                  <Grid
                    item
                    xs={12}
                    sm={5.8}
                    lg={5.86}
                    key={`feature-${actualIndex}`}
                    className="grid-item"
                    sx={{ display: 'flex', flex: '1 1 calc(50% - 8px)' }}
                  >
                    {renderFeatureBox(card, actualIndex, false)}
                  </Grid>
                );
              })}
            </Grid>
          )}
          {isMainExpanded && (
            <Grid container spacing={2}>
              {cardsData.slice(1).map((card, index) => {
                const actualIndex = index + 1;
                return (
                  <Grid
                    item
                    xs={12}
                    key={`feature-${actualIndex}`}
                    className="grid-item"
                  >
                    {renderFeatureBox(card, actualIndex, false)}
                  </Grid>
                );
              })}
            </Grid>
          )}
          {isOtherExpanded && (
            <div className="grid-wrapper-expanded">
              {renderFeatureBox(
                cardsData[expandedIndex!],
                expandedIndex!,
                true
              )}
            </div>
          )}
        </Grid>
      </Grid>
    </section>
  );
}

function FeatureGrid() {

  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = !isMobile && !isTablet;

  if (isDesktop) {
    return <FeatureDesktopLayout />;
  }
  return <DynamicAccordionLayout />;
}

export default FeatureGrid;